import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaEye } from "react-icons/fa";
import {
  Box,
  Flex,
  Text,
  Button,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Spinner,
  Alert,
  AlertIcon,
  HStack,
  Select,
  useColorModeValue,
  Heading,
  Card,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { fetchEquipmentData } from "../../redux/EquipmentSlice";
import TableView from "../../components/TableView";
import { FiEdit, FiEye } from "react-icons/fi";
import CustomToolTip from "../../components/CustomTooltip";

export default function EquipmentMaster() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data, loading, error, totalcount } = useSelector(
    (state) => state.equipment
  );

  const [activeTab, setActiveTab] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const tabs = ["all", "underReview", "approved", "rejected"];

  useEffect(() => {
    dispatch(fetchEquipmentData({ page: currentPage, limit: rowsPerPage }));
  }, [dispatch, currentPage, rowsPerPage]);

  const allEquipment = data || [];

  const underReviewEquipment = allEquipment.filter(
    (item) =>
      !item.details ||
      item.details.length === 0 ||
      item.details.some((detail) => String(detail.status) === "0")
  );

  const approvedEquipment = allEquipment.filter(
    (item) =>
      item.details &&
      item.details.some((detail) => String(detail.status) === "2")
  );

  const rejectedEquipment = allEquipment.filter(
    (item) =>
      item.details &&
      item.details.length > 0 &&
      item.details.every((detail) => String(detail.status) === "1")
  );

  const bgCard = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const primaryColor = "#347433";
  const primaryLight = "green.50";

  const getStatusBadge = (status) => {
    switch (status) {
      case "0":
        return { color: "yellow", text: "Under Review" };
      case "1":
        return { color: "red", text: "Rejected" };
      case "2":
        return { color: "green", text: "Approved" };
      default:
        return { color: "gray", text: "Unknown" };
    }
  };

  const tableColumns = [
    { header: "SR Number", key: "sr_no" },
    { header: "Date", key: "sr_date" },
    { header: "Customer Name", key: "customer_name" },
    { header: "Status", key: "status" },
    { header: "Actions", key: "actions" },
  ];

  const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          size="sm"
          variant={currentPage === i ? "solid" : "outline"}
          colorScheme={currentPage === i ? "green" : "gray"}
          onClick={() => onPageChange(i)}
        >
          {i}
        </Button>
      );
    }

    return (
      <HStack
        spacing={2}
        mt={4}
        justify={{ base: "center", md: "flex-end" }}
        flexWrap="wrap"
      >
        <Button
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          isDisabled={currentPage === 1}
          leftIcon={<ChevronLeftIcon />}
        >
          Previous
        </Button>

        {pages}

        <Button
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          isDisabled={currentPage === totalPages}
          rightIcon={<ChevronRightIcon />}
        >
          Next
        </Button>
      </HStack>
    );
  };

  const renderStatusTable = (equipment, statusName) => {
    if (equipment.length === 0) {
      return (
        <Box bg="#fff" p={6} textAlign="center">
          <Text fontSize="sm" color="gray.500">
            No data available
          </Text>
        </Box>
      );
    }
  
    const startIndex = (currentPage - 1) * rowsPerPage;
  
    return (
      <TableView
        headData={[
          "SR No",
          "SR Date",
          "Customer Name",
          "Status",
          "Actions",
        ]}
        body={equipment.map((item, idx) => {
          let overallStatus = "0";
          let statusText = "Under Review";
  
          if (item.details && item.details.length > 0) {
            const hasApproved = item.details.some(
              (detail) => String(detail.status) === "2"
            );
            const allRejected = item.details.every(
              (detail) => String(detail.status) === "1"
            );
            const hasUnderReview = item.details.some(
              (detail) => String(detail.status) === "0"
            );
          
            // ✅ Normal logic
            if (hasApproved) {
              overallStatus = "2";
              statusText = "Approved";
            } else if (allRejected) {
              overallStatus = "1";
              statusText = "Rejected";
            } else if (hasUnderReview) {
              overallStatus = "0";
              statusText = "Under Review";
            }
          
            // ✅ Extra rule: if this is being rendered inside "Under Review" tab,
            // force badge to show "Under Review" regardless of any approved records
            if (statusName === "underReview" && hasUnderReview) {
              overallStatus = "0";
              statusText = "Under Review";
            }
          }
          
          const statusBadge = getStatusBadge(overallStatus);
  
          return (
            <Tr key={idx}>
              <Td textAlign="center">
                {item.sr_no || `EQ-${item.id}`}
              </Td>
              <Td textAlign="center">
                {item.sr_date
                  ? new Date(item.sr_date).toLocaleDateString()
                  : "N/A"}
              </Td>
              <Td textAlign="center">
                {item.customer_name || "N/A"}
              </Td>
              <Td textAlign="center">
                <Badge
                  colorScheme={statusBadge.color}
                  borderRadius="md"
                  px={2}
                  py={1}
                >
                  {statusText}
                </Badge>
              </Td>
              <Td textAlign="center">
              <CustomToolTip
                      button={
                        <FiEye
                          size={20}
                          cursor={"pointer"}
                          onClick={() =>
                            navigate(
                              `/equipment-master/equipment-master-detail/${item.id}`,
                              {
                                state: {
                                  previousTab: activeTab,
                                  previousTabName: tabs[activeTab],
                                  isViewOnly:true,
                                },
                            }
                            )
                          }
                        />

                      }
                      mess={"View Equipment"}
                    />
  <CustomToolTip
                      button={
                        <FiEdit
                          size={20}
                          cursor={"pointer"}
                          onClick={() =>
                            navigate(
                              `/equipment-master/equipment-master-detail/${item.id}`,
                              {
                                state: {
                                  previousTab: activeTab,
                                  previousTabName: tabs[activeTab],
                                  isViewOnly:false,
                                },
                            }
                            )
                          }
                        />

                      }
                      mess={"View Details"}
                    />
                {/* <Button
                  size="xs"
                  color="white"
                  onClick={() =>
                    navigate(
                      `/equipment-master/equipment-master-detail/${item.id}`,
                      {
                        state: {
                          previousTab: activeTab,
                          previousTabName: tabs[activeTab],
                        },
                      }
                    )
                  }
                >
                  <FaEye size={12} />
                </Button> */}
              </Td>
            </Tr>
          );
        })}
        initialValue={startIndex + 1}
        page={currentPage}
        rows={rowsPerPage}
        total_value={totalcount}
        pagination
        onChange={(e) => {
          setRowsPerPage(Number(e.target.value));
          setCurrentPage(1);
        }}
        onPrevious={() => {
          if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
          }
        }}
        onNext={() => {
          if (rowsPerPage * currentPage < totalcount) {
            setCurrentPage(currentPage + 1);
          }
        }}
      />
    );
  };

  return (
    <>
      <Card
        justify="space-between"
        align="center"
        paddingInline={5}
        paddingBlock={2}
        borderRadius="lg" 
        mb={3}
        flexDir={{ base: "column", sm: "row" }}
        gap={2}
        fontWeight="bold"
        fontSize={{ base: "md", md: "lg" }}
      >
        <Text>
          Equipment Master Dashboard
        </Text>
      </Card>

      <Tabs
        variant="soft-rounded"
        colorScheme="green"
        index={activeTab}
        onChange={(index) => {
          setActiveTab(index);
          setCurrentPage(1);
        }}
        isFitted
      >
        <TabList overflowX="auto" flexWrap="nowrap">
          <Tab
            fontWeight="medium"
            fontSize={{ base: "sm", md: "15px" }}
            _selected={{ color: "white", bg: primaryColor }}
          >
            All Equipment
          </Tab>
          <Tab
            fontWeight="medium"
            fontSize={{ base: "sm", md: "15px" }}
            _selected={{ color: "white", bg: primaryColor }}
          >
            Under Review
          </Tab>
          <Tab
            fontWeight="medium"
            fontSize={{ base: "sm", md: "15px" }}
            _selected={{ color: "white", bg: primaryColor }}
          >
            Approved
          </Tab>
          <Tab
            fontWeight="medium"
            fontSize={{ base: "sm", md: "15px" }}
            _selected={{ color: "white", bg: primaryColor }}
          >
            Rejected
          </Tab>
        </TabList>

        <TabPanels mt={4}>
          <TabPanel p={0}>
            {loading ? (
              <Flex justify="center" my={8}>
                <Spinner size="xl" color={primaryColor} />
              </Flex>
            ) : error ? (
              <Alert status="error" my={4}>
                <AlertIcon />
                Error loading equipment data: {error}
              </Alert>
            ) : allEquipment.length === 0 ? (
              <Box
                bg={bgCard}
                rounded="lg"
                shadow="sm"
                p={8}
                textAlign="center"
                borderWidth="1px"
                borderColor={borderColor}
              >
                <Text fontSize="lg" color="gray.500">
                  No equipment applications found
                </Text>
              </Box>
            ) : (
              renderStatusTable(allEquipment, "all")
            )}
          </TabPanel>

          <TabPanel p={0}>
            {loading ? (
              <Flex justify="center" my={8}>
                <Spinner size="xl" color={primaryColor} />
              </Flex>
            ) : error ? (
              <Alert status="error" my={4}>
                <AlertIcon />
                Error loading equipment data: {error}
              </Alert>
            ) : underReviewEquipment.length === 0 ? (
              <Box
                bg={bgCard}
                rounded="lg"
                shadow="sm"
                p={8}
                textAlign="center"
                borderWidth="1px"
                borderColor={borderColor}
              >
                <Text fontSize="lg" color="gray.500">
                  No equipment under review
                </Text>
              </Box>
            ) : (
              renderStatusTable(underReviewEquipment, "underReview")
            )}
          </TabPanel>

          <TabPanel p={0}>
            {loading ? (
              <Flex justify="center" my={8}>
                <Spinner size="xl" color={primaryColor} />
              </Flex>
            ) : error ? (
              <Alert status="error" my={4}>
                <AlertIcon />
                Error loading equipment data: {error}
              </Alert>
            ) : approvedEquipment.length === 0 ? (
              <Box
                bg={bgCard}
                rounded="lg"
                shadow="sm"
                p={8}
                textAlign="center"
                borderWidth="1px"
                borderColor={borderColor}
              >
                <Text fontSize="lg" color="gray.500">
                  No approved equipment
                </Text>
              </Box>
            ) : (
              renderStatusTable(approvedEquipment, "approved")
            )}
          </TabPanel>

          <TabPanel p={0}>
            {loading ? (
              <Flex justify="center" my={8}>
                <Spinner size="xl" color={primaryColor} />
              </Flex>
            ) : error ? (
              <Alert status="error" my={4}>
                <AlertIcon />
                Error loading equipment data: {error}
              </Alert>
            ) : rejectedEquipment.length === 0 ? (
              <Box
                bg={bgCard}
                rounded="lg"
                shadow="sm"
                p={8}
                textAlign="center"
                borderWidth="1px"
                borderColor={borderColor}
              >
                <Text fontSize="lg" color="gray.500">
                  No rejected equipment
                </Text>
              </Box>
            ) : (
              renderStatusTable(rejectedEquipment, "rejected")
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </>
  );
}
